<template>
    <div class="card card-height">
        <div class="row">
            <div class="col-12 col-md-6">
                <div class="table-responsive">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Particulars</th>
                                <th class="text-end">Amount</th>
                                <th class="text-end">Total</th>
                            </tr>
                        </thead>
                        <tbody v-for="(item, i) in service" :key="i">
                            <tr class="bg-blue-light">
                                <td colspan="3">
                                    <span class="text-decoration-underline">{{ item.name }}</span>
                                </td>
                            </tr>
                            <tr
                                v-for="(el, index) in item.child"
                                :key="index"
                                :class="{ 'return-row': el.is_return }"
                            >
                                <td>{{ el.name }}</td>
                                <td class="text-end">{{ formatSigned(el.total_amount) }}</td>
                                <td v-if="index === 0" :rowspan="item.child.length + 1" class="text-end fw-bold">
                                    {{ formatSigned(childTotal(item.child)) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                    <p v-if="!service.length" class="text-center mt-5">No bill found</p>

                    <div v-if="service.length" class="row py-2 g-3">
                        <div class="col-12 col-md-6 d-flex align-items-center">
                            <span v-if="isDue" class="badge rounded-pill badge-light-danger status-pill">Due</span>
                            <span v-if="!isDue" class="badge rounded-pill badge-light-success status-pill">Paid</span>
                        </div>
                        <div class="col-12 col-md-6">
                            <div class="summary-panel">
                                <div class="summary-row">
                                    <span class="summary-label">Total Amount</span>
                                    <span class="summary-value">{{ commaFormat(totalAfterReturn) }}</span>
                                </div>
                                <div class="summary-row">
                                    <span class="summary-label">(-) Bill Discount</span>
                                    <span class="summary-value">{{ commaFormat(total.discount) }}</span>
                                </div>
                                <div class="summary-row">
                                    <span class="summary-label">(-) Settlement Discount</span>
                                    <span class="summary-value">{{ commaFormat(total.settlement_discount) }}</span>
                                </div>
                                <div class="summary-row summary-row-total">
                                    <span class="summary-label">Net Bill Amount</span>
                                    <span class="summary-value">{{ commaFormat(netAmount) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="receiptGenerals.length" class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>MR Date &amp; Time</th>
                                    <th>MR No</th>
                                    <th>Mode of payment</th>
                                    <th>MoP Ref.</th>
                                    <th>Receipt by</th>
                                    <th class="text-end">Receipt Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in receiptGenerals" :key="index">
                                    <td>{{ formatDateWithTime(item.created_at) }}</td>
                                    <td>{{ item.voucher_no }}</td>
                                    <td>{{ headName(item) }}</td>
                                    <td>{{ item.receipt_reference }}</td>
                                    <td>{{ userName(item) }}</td>
                                    <td class="text-end">{{ commaFormat(item.total_paid_amount) }}</td>
                                </tr>
                                <tr>
                                    <td colspan="5" class="text-end">Total</td>
                                    <td class="text-end">{{ commaFormat(totalPaidAmount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            <div class="col-12 col-md-6">
                <div class="row g-1">
                    <div v-if="isNotSettled" class="col-12 col-lg-6">
                        <div class="summary-panel">
                            <MoneyReceiptForm :formData="formData" :grand-total="netPayable"/>
                        </div>
                    </div>
                    <div v-if="service.length" class="col-12 col-lg-6">
                        <div class="summary-panel">
                            <div class="summary-row">
                                <span class="summary-label">Net Bill Amount</span>
                                <span class="summary-value">{{ commaFormat(netAmount) }}</span>
                            </div>
                            <div class="summary-row">
                                <span class="summary-label">Total Paid</span>
                                <span class="summary-value">{{ commaFormat(totalPaidAmount) }}</span>
                            </div>
                            <div class="summary-row">
                                <span class="summary-label">Due / Return Amount</span>
                                <span class="summary-value">{{ commaFormat(formData.bill_amount) }}</span>
                            </div>
                            <div v-if="isNotSettled" class="summary-row summary-row-input">
                                <span class="summary-label">Discount / Round up Adj</span>
                                <div class="summary-input-group">
                                    <input
                                        v-model.trim="discountPercent"
                                        type="number"
                                        class="form-control form-control-sm discount-adj text-end"
                                        placeholder="%"
                                        :min="0"
                                    >
                                    <input
                                        v-model.trim="discountAmount"
                                        type="number"
                                        class="form-control form-control-sm discount-adj text-end"
                                        placeholder="amount"
                                        :min="0"
                                    >
                                </div>
                            </div>
                            <div v-if="discountAmount > 0" class="summary-row summary-row-input">
                                <span class="summary-label">Select discount head</span>
                                <v-select
                                    placeholder="Select Discount Head"
                                    label="name"
                                    v-model="formData.discount_head_id"
                                    :options="salesAccountHeads"
                                    :reduce="name => name.id"
                                    class="summary-select"
                                />
                            </div>
                            <div class="summary-row summary-row-total">
                                <span class="summary-label">Net Payable</span>
                                <span class="summary-value">{{ commaFormat(netPayable) }}</span>
                            </div>
                            <div class="summary-row">
                                <span class="summary-label">Paid Amount</span>
                                <span class="summary-value">{{ commaFormat(formData.payment) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-12 d-flex justify-content-center">
            <button
                v-if="isNotSettled"
                class="btn btn-success"
                :disabled="disableStlBtn"
                @click="handleSettlement"
            >
                Add Final Settlement
            </button>
        </div>
      <div class="col-12 d-flex justify-content-start">
        <button
            v-if="isSettled"
            class="btn btn-secondary"
            @click="navigateToUpdatePage"
        >
          Update Final Settlement
        </button>
      </div>
    </div>
</template>

<script setup>
import MoneyReceiptForm from '@/components/molecule/company/hospital/ipd-station/MoneyReceiptForm.vue';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import figureFormatter from '@/services/utils/figureFormatter';
import useDate from '@/services/utils/day';
import handleHospital from '@/services/modules/hospital';
import handlePurchase from '@/services/modules/purchase';

const props = defineProps({
    service: {
        type: Array,
        default: () => [],
    },
    total: {
        type: Object,
        default: () => ({}),
    },
    receiptGenerals: {
        type: Array,
        default: () => [],
    },
    patientDetails: {
        type: Object,
        default: () => ({}),
    },
    reopenCheckoutId: {
        type: Number,
        default: null,
    },
});

const emit = defineEmits(['onSettled']);

const $route = useRoute();
const $router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { commaFormat } = figureFormatter();
const { formatDateWithTime } = useDate();
const { storeSettlement } = handleHospital();
const { getAccountHeadBySlag } = handlePurchase();

const discountPercent = ref(null);
const discountAmount = ref(null);
const disableStlBtn = ref(false);
const salesAccountHeads = ref([]);
const formData = ref({
    date: new Date().toISOString().split('T')[0],
    company_id: $route.params.companyId,
    contact_profile_id: null,
    ipd_register_id: null,
    receivable_account_head_id: null,
    mop_account_head_id: null,
    discount_amount: 0,
    discount_head_id: null,
    reference: '',
    payment: 0,
    bill_amount: 0,
});

const isNotSettled = computed(() => props.patientDetails.settlement_status === 'not_settled');

const isSettled = computed(() => props.patientDetails.settlement_status === 'settled');

const getRegisterId = () => {
    return $route.params.ipdRegisterId || $route.query.ipd_register_id;
};

const buildBackUrl = () => {
    if (props.reopenCheckoutId) {
        const resolved = $router.resolve({
            name: 'hotel-frontdesk',
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId,
            },
            query: {
                tab: 'pending_checkout',
                reopen_checkout: props.reopenCheckoutId,
            },
        });
        return resolved.fullPath;
    }
    const resolved = $router.resolve({
        name: 'hotel-bill-statement',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            ipdRegisterId: getRegisterId(),
            pageId: $route.params.pageId,
        },
        query: { tab: 'statement' },
    });
    return resolved.fullPath;
};

const navigateToUpdatePage = () => {
    const query = {
        tab: 'statement',
        id: props.patientDetails.id,
        back_url: encodeURIComponent(buildBackUrl()),
    };
    $router.push({
        name: 'ipd-statement-edit',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: query,
    });
};

const totalPaidAmount = computed(() => {
    let sum = 0;
    for (const item of props.receiptGenerals) {
        sum += Number(item.total_paid_amount);
    }
    return sum;
});

const returnAmount = computed(() => Number(props.total.return_amount || 0));

const totalAfterReturn = computed(() => Number(props.total.amount || 0) - returnAmount.value);

const netAmount = computed(() => {
    const discount = Number(props.total.discount || 0);
    const settlementDiscount = Number(props.total.settlement_discount || 0);
    return totalAfterReturn.value - discount - settlementDiscount;
});

const netPayable = computed(() => (netAmount.value - totalPaidAmount.value) - Number(discountAmount.value || 0));

const isDue = computed(() => netAmount.value > totalPaidAmount.value);

const childTotal = (child) => {
    let sum = 0;
    for (const item of child) {
        sum += Number(item.total_amount);
    }
    return sum;
};

const formatSigned = (value) => {
    if (value < 0) return `(${commaFormat(Math.abs(value))})`;
    return commaFormat(value);
};

const headName = (item) => {
    if (!item.account_head) return '';
    return item.account_head.name;
};

const userName = (item) => {
    if (!item.created_user) return '';
    return item.created_user.name;
};

const resolveDiscountHead = () => {
    if (!(discountAmount.value > 0)) return null;
    if (!salesAccountHeads.value.length) return null;
    return salesAccountHeads.value[0].id;
};

watch(discountPercent, () => {
    if (Number(discountPercent.value) === 0) {
        discountAmount.value = 0;
        return;
    }
    discountAmount.value = ((netAmount.value - totalPaidAmount.value) * Number(discountPercent.value)) / 100;
});

watch(netAmount, () => {
    formData.value.bill_amount = netAmount.value - totalPaidAmount.value;
}, { immediate: true });

watch(netPayable, (newValue) => {
    formData.value.payment = newValue;
}, { immediate: true });

watch(discountAmount, (newValue) => {
    if (newValue < 0) {
        showError('Discount can not be less than 0');
        discountAmount.value = 0;
    }
    formData.value.discount_amount = parseFloat(discountAmount.value);
    formData.value.discount_head_id = resolveDiscountHead();
});

watch(() => props.patientDetails, (newValue) => {
    if (!newValue) return;
    formData.value.contact_profile_id = newValue.contact_profile_id;
    formData.value.ipd_register_id = newValue.id;
}, { deep: true, immediate: true });

const handleSettlement = () => {
    disableStlBtn.value = true;
    storeSettlement(formData.value.ipd_register_id, formData.value).then(res => {
        if (!res.status) {
            showError(res.message);
            disableStlBtn.value = false;
            return;
        }
        showSuccess(res.message);
        emit('onSettled');
    }).catch(() => {
        showError('Something went wrong');
        disableStlBtn.value = false;
    });
};

onMounted(() => {
    const companyQuery = `?company_id=${$route.params.companyId}`;
    getAccountHeadBySlag('discount_account', companyQuery).then(res => {
        if (res.data) salesAccountHeads.value = res.data;
    });
});
</script>

<style scoped>
.status-pill {
    font-size: 16px;
    font-weight: 600;
    padding: 0.5rem 1.25rem;
}
.summary-panel {
    border: 1px solid #e4e4e4;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    background-color: #fbfbfb;
}
.summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #eee;
}
.summary-row:last-child {
    border-bottom: none;
}
.summary-row-total {
    border-top: 1px solid #ccc;
    border-bottom: none;
    margin-top: 0.25rem;
    padding-top: 0.75rem;
}
.summary-row-total .summary-label,
.summary-row-total .summary-value {
    font-size: 1.05rem;
}
.summary-row-input {
    flex-direction: column;
    align-items: stretch;
}
.summary-label {
    font-weight: 600;
    color: #444;
}
.summary-value {
    font-weight: 600;
    text-align: right;
}
.summary-input-group {
    display: flex;
    gap: 0.5rem;
}
.summary-select {
    width: 100%;
}
.discount-adj {
    width: 100%;
}
.return-row td {
    background-color: #fde8e8;
    color: #b02a2a;
}
@media (max-width: 767px) {
    .summary-panel {
        padding: 0.75rem 1rem;
    }
}
</style>
